<template>
	<div class="content ra">
		<div class="section">
			<div class="section-tit">
				Weekly Report
				<span class="sub">{{ tab === 'leads' ? leadSummary : summary }}</span>
				<div class="right" v-if="tab === 'list'">
					<input type="text" v-model="kw" class="form-control" placeholder="제목 검색" @keyup.enter="loadList" />
					<button type="button" class="btn btn-sm btn-secondary" @click="loadList">검색</button>
				</div>
				<div class="right" v-else>
					<input type="text" v-model="leadKw" class="form-control" placeholder="고객·Weekly 검색" />
				</div>
			</div>

			<!-- 매매검색에서 매물을 골라 들어온 경우. 새로 만들 수도, **이미 있는 Weekly 에 더할 수도** 있다
			     (2026-09-23 사용자 지적 "위클리 생성만 되고 물건지 추가가 안됨. 연속적으로만 만들어짐").
			     콘솔의 입력 표(라벨칸 + 값칸)다. 고칠 수 있는 Weekly 가 있으면 '추가' 가 기본 동작(파랑)이다. -->
			<div class="ra-new" v-if="newBids.length">
				<div class="section-tit sub">선택한 매물 {{ newBids.length }}건 담기</div>
				<table class="table type-input">
					<colgroup><col class="ra-col-label" /><col /></colgroup>
					<tr>
						<th>담을 매물</th>
						<td>
							<b>{{ newBids.length }}건</b>
							<span class="ra-note">번호 {{ newBids.join(', ') }}</span>
						</td>
					</tr>
					<tr>
						<th>기존 Weekly 에 추가</th>
						<td>
							<div class="ra-row" v-if="targets.length">
								<select v-model.number="targetUid" class="form-select form-select-sm ra-target">
									<option v-for="t in targets" :key="t.report_uid" :value="t.report_uid" :disabled="Number(t.item_cnt) >= maxItems">
										{{ targetLabel(t) }}
									</option>
								</select>
								<button type="button" class="btn btn-sm btn-primary" @click="addToReport" :disabled="busy || !targetUid">
									{{ adding ? '담는 중…' : '이 Weekly 에 추가' }}
								</button>
							</div>
							<div class="ra-note ra-note--block" :class="{ warn: roomWarn }" v-if="targets.length">
								{{ roomText }}
							</div>
							<span class="ra-note ra-note--flush" v-else-if="targetsLoaded">
								추가할 수 있는 Weekly 가 없습니다.<template v-if="!isMaster"> 매물은 내가 만든 Weekly 에만 더할 수 있습니다(공용 Weekly 는 관리자만 고칩니다).</template>
							</span>
						</td>
					</tr>
					<tr>
						<th>새 Weekly 만들기</th>
						<td>
							<div class="ra-row">
								<input type="text" v-model="newTitle" class="form-control ra-title" :placeholder="defaultTitle" maxlength="120" @keyup.enter="createReport" />
								<button type="button" class="btn btn-sm" :class="targets.length ? 'btn-secondary' : 'btn-primary'" @click="createReport" :disabled="busy">
									{{ creating ? '만드는 중…' : '새 Weekly 생성' }}
								</button>
							</div>
							<!-- 공용 Weekly 등록은 관리자만(DE). 서버도 관리자가 아니면 거절한다. -->
							<label class="ra-check" v-if="isMaster">
								<input type="checkbox" v-model="newShared" :disabled="busy" />
								공용 Weekly 로 등록 <span class="ra-note ra-note--flush">Weekly 권한이 있는 모든 직원의 목록에 보이고, 각자 자기 고객 링크를 발급합니다.</span>
							</label>
							<div class="ra-note ra-note--block" :class="{ warn: !inRecommended }">
								권장 구성은 4~6건입니다{{ inRecommended ? '' : '. 지금은 권장 범위 밖입니다' }}.
								서로 다른 지역·성격의 물건을 함께 담을수록 고객의 관심 범위가 넓어집니다.
							</div>
						</td>
					</tr>
				</table>
			</div>

			<!-- 1차 전환: 리포트 목록 / 고객 리드. 담당자별 거르기는 그 아래 2차 칩이다(DESIGN.md). -->
			<div class="tab-btn">
				<button type="button" class="btn tab" :class="{ active: tab === 'list' }" @click="setTab('list')">Weekly 목록</button>
				<button type="button" class="btn tab" :class="{ active: tab === 'leads' }" @click="setTab('leads')">
					{{ allLinks ? '고객 리드' : '내 고객 리드' }}
				</button>
			</div>

			<!-- ── Weekly 목록 ── 클릭하면 편집으로. 지면(테마) 열은 어떤 리포트가 어떤 지면으로 나갔는지
			     한눈에 대조하는 자리다(색 견본 하나와 이름). 링크·열람 수는 **내가 볼 수 있는 고객만** 센다(CR). -->
			<div class="tab-con" v-if="tab === 'list'">
				<!-- 2차 거르기(DESIGN.md: 탭이 아니라 칩). 직원은 자기가 만든 것과 관리자가 지정한 공용만 보인다(DE). -->
				<div class="ra-staff">
					<span class="ra-staff__label">구분</span>
					<button v-for="k in KINDS" :key="'k' + k.v" type="button" class="btn ra-staff__chip" :class="{ on: kind === k.v }" @click="pickKind(k.v)">
						{{ k.label }}
					</button>
					<template v-if="isMaster && authors.length">
						<span class="ra-staff__label ra-staff__label--gap">작성자</span>
						<select class="form-select form-select-sm ra-owner" :value="owner || ''" @change="pickOwner($event.target.value)">
							<option value="">전체</option>
							<option v-for="a in authors" :key="'a' + a.mem_uid" :value="a.mem_uid">{{ a.name }} ({{ a.cnt }})</option>
						</select>
					</template>
				</div>
				<p class="ra-scope" v-if="!isMaster">
					내가 만든 Weekly 와 관리자가 등록한 공용 Weekly 만 보입니다. 공용 Weekly 는 열어서 내 고객 링크를 발급할 수 있고, 내용은 관리자만 고칩니다.
				</p>
				<div class="table-wrap">
					<table class="table sm click">
						<colgroup>
							<col style="width: 96px" />
							<col />
							<col style="width: 150px" />
							<col style="width: 64px" />
							<col style="width: 88px" />
							<col style="width: 88px" />
							<col style="width: 110px" />
							<col style="width: 100px" />
							<col style="width: 72px" />
							<col v-if="isMaster" style="width: 92px" />
						</colgroup>
						<thead>
							<tr>
								<th>주차</th>
								<th>제목</th>
								<th>지면</th>
								<th>매물</th>
								<th>{{ allLinks ? '발급 링크' : '내 고객 링크' }}</th>
								<th>{{ allLinks ? '열람' : '내 고객 열람' }}</th>
								<th>작성자</th>
								<th>작성일</th>
								<th>상태</th>
								<th v-if="isMaster">공용</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="r in list" :key="r.report_uid" class="click" @click="openEdit(r.report_uid)">
								<td class="num">{{ r.period_key }}</td>
								<td class="left ra-title-cell">
									<b><span class="ra-shared" v-if="r.shared">공용</span>{{ r.title }}</b>
									<span class="ra-period">{{ r.sdate }} ~ {{ r.edate }}{{ !r.shared && !r.mine && !isMaster ? ' · 공용 해제됨(내가 보낸 링크만 봅니다)' : '' }}</span>
								</td>
								<td class="left">
									<span class="ra-sw" :class="'ra-sw--' + themeOf(r.theme).id" aria-hidden="true"><i></i><i></i></span>
									{{ themeOf(r.theme).name }}
								</td>
								<td class="num">{{ r.item_cnt }}건</td>
								<td class="num" :class="{ zero: !Number(r.link_cnt) }">{{ r.link_cnt }}개</td>
								<td class="num" :class="{ zero: !Number(r.view_cnt) }">{{ r.view_cnt }}회</td>
								<td>{{ r.created_name || '-' }}</td>
								<td class="num">{{ ymd(r.created_at) }}</td>
								<td>
									<span class="ra-status" :class="r.status === 'published' ? 'is-pub' : 'is-draft'">
										{{ r.status === 'published' ? '발행' : '작성중' }}
									</span>
								</td>
								<td v-if="isMaster" @click.stop>
									<button type="button" class="btn btn-xxsm" :class="r.shared ? 'btn-light' : 'btn-secondary'" :disabled="sharing === r.report_uid" @click="toggleShare(r)">
										{{ r.shared ? '공용 해제' : '공용으로' }}
									</button>
								</td>
							</tr>
							<tr v-if="!list.length">
								<td :colspan="isMaster ? 10 : 9" class="ra-empty">
									<template v-if="kind === 'shared'">공용 Weekly 가 없습니다.{{ isMaster ? ' 목록의 공용으로 를 누르거나, 새로 만들 때 공용으로 등록하세요.' : '' }}</template>
									<template v-else-if="kw || owner || kind === 'mine'">조건에 맞는 리포트가 없습니다.</template>
									<template v-else>아직 만든 리포트가 없습니다. 매매검색에서 매물을 고른 뒤 <b>Weekly 담기</b> 를 누르면 여기에 쌓입니다.</template>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<p class="table-text ra-guide" v-if="list.length && !newBids.length">
					매물은 매매검색에서 고른 뒤 <b>Weekly 담기</b> 로 새 리포트를 만들거나 기존 리포트에 더합니다.
					<template v-if="isMaster"><b>공용으로</b> 지정한 Weekly 는 Weekly 권한이 있는 모든 직원의 목록에 보이고, 각자 자기 고객 링크를 발급합니다.</template>
				</p>
			</div>

			<!-- ── 고객 리드 ── 리포트를 가로질러 "내 고객이 무엇을 언제 얼마나 봤는가". 직원은 자기 고객만,
			     어드민(master)은 전체를 담당자 칩으로 걸러 본다(서버 linkScope). 최근에 연 고객이 위로 온다. -->
			<div class="tab-con" v-else>
				<div class="ra-staff" v-if="allLinks && leadStaff.length">
					<span class="ra-staff__label">담당자별</span>
					<button type="button" class="btn ra-staff__chip" :class="{ on: !leadMem }" @click="pickLeadStaff(null)">
						전체<span class="ra-staff__cnt">{{ leadStaffTotal }}</span>
					</button>
					<button
						v-for="st in leadStaff"
						:key="'ls' + st.mem_uid"
						type="button"
						class="btn ra-staff__chip"
						:class="{ on: Number(leadMem) === Number(st.mem_uid) }"
						:title="`${st.name} 고객 ${st.link_cnt}명 · 열어 본 고객 ${st.viewed_cnt}명`"
						@click="pickLeadStaff(st.mem_uid)">
						{{ st.name }}<span class="ra-staff__cnt">{{ st.viewed_cnt }}/{{ st.link_cnt }}</span>
					</button>
					<span class="ra-staff__legend">숫자는 열어 본 고객 / 발급한 링크</span>
				</div>
				<p class="ra-scope" v-if="!allLinks">
					내가 발급한 고객 링크만 보입니다. 다른 담당자의 고객은 보이지 않습니다(전체는 관리자만 봅니다).
				</p>

				<div class="table-wrap">
					<table class="table sm click ra-leads">
						<colgroup>
							<col style="width: 150px" />
							<col v-if="allLinks" style="width: 112px" />
							<col />
							<col style="width: 100px" />
							<col style="width: 136px" />
							<col style="width: 64px" />
							<col style="width: 84px" />
							<col style="width: 220px" />
							<col style="width: 56px" />
							<col style="width: 76px" />
						</colgroup>
						<thead>
							<tr>
								<th>고객</th>
								<th v-if="allLinks">담당자</th>
								<th>Weekly</th>
								<th>발급일</th>
								<th>최근 열람</th>
								<th>열람</th>
								<th>총 체류</th>
								<th>가장 오래 본 매물</th>
								<th>PDF</th>
								<th>상태</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="l in shownLeads" :key="l.link_uid" class="click" :class="{ 'is-off': l.revoked_at || expired(l) }" @click="openEdit(l.report_uid)">
								<td class="left"><b>{{ l.client_label }}</b></td>
								<td v-if="allLinks" class="nowrap">{{ l.created_name }}</td>
								<td class="left ra-title-cell">
									<b>{{ l.title }}</b>
									<span class="ra-period">{{ l.period_key }}{{ l.report_status === 'published' ? '' : ' · 작성중' }}</span>
								</td>
								<td class="num">{{ ymd(l.created_at) }}</td>
								<td class="num" :class="{ zero: !l.last_viewed_at }">{{ l.last_viewed_at || '안 열어 봄' }}</td>
								<td class="num" :class="{ zero: !Number(l.view_cnt) }">{{ l.view_cnt }}회</td>
								<td class="num">{{ dur(l.active_ms) }}</td>
								<td class="left">
									<template v-if="l.top_item">
										<span class="ra-top" :title="l.top_item.name">{{ l.top_item.name }}</span>
										<span class="ra-period">{{ dur(l.top_item.visible_ms) }} · 본 매물 {{ l.viewed_items }}건</span>
									</template>
									<span v-else class="ra-muted">-</span>
								</td>
								<td class="num">{{ Number(l.pdf_cnt) ? l.pdf_cnt + '회' : '-' }}</td>
								<td>
									<span v-if="l.revoked_at" class="ra-status is-draft">회수됨</span>
									<span v-else-if="expired(l)" class="ra-status is-draft">기간만료</span>
									<span v-else class="ra-status is-pub">유효</span>
								</td>
							</tr>
							<tr v-if="leadsLoaded && !shownLeads.length">
								<td :colspan="allLinks ? 10 : 9" class="ra-empty">
									<template v-if="leadKw">검색어에 맞는 고객이 없습니다.</template>
									<template v-else-if="allLinks && leadMem">이 담당자가 발급한 고객 링크가 없습니다.</template>
									<template v-else>아직 발급한 고객 링크가 없습니다. Weekly 를 열어 <b>링크 발급</b> 을 누르면 여기에 쌓입니다.</template>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<p class="table-text ra-guide" v-if="leadsTruncated">최근 활동 순으로 {{ leads.length }}명까지 보여 줍니다.</p>
			</div>
		</div>
	</div>
</template>

<script>
import { themeOf } from '@/utils/reportThemes';

const KINDS = [
	{ v: 'all', label: '전체' },
	{ v: 'mine', label: '내가 만든 Weekly' },
	{ v: 'shared', label: '공용 Weekly' },
];

export default {
	name: 'ReportAdmin',
	data() {
		return {
			KINDS,
			tab: 'list',
			list: [],
			kw: '',
			// 목록 거르기(DE): all | mine | shared. 관리자는 작성자로도 거른다(owner).
			kind: 'all',
			owner: null,
			authors: [],
			isMaster: (this.$store.getters.getUserInfo || {}).mem_type === 'master',
			newShared: false,
			sharing: null,
			allLinks: false,
			linkLevel: 'self',
			maxItems: 10,
			newBids: [],
			newType: 'sell',
			newTitle: '',
			creating: false,
			adding: false,
			// '기존 Weekly 에 추가' 대상. 고칠 수 있는 것만 온다(서버 editable=1).
			targets: [],
			targetsLoaded: false,
			targetUid: null,
			leads: [],
			leadsLoaded: false,
			leadsTruncated: false,
			leadStaff: [],
			leadMem: null,
			leadKw: '',
			leadSeq: 0,
			listSeq: 0,
		};
	},
	computed: {
		busy() {
			return this.creating || this.adding;
		},
		/** 기획 의도인 4~6건 범위인지. 벗어나도 막지는 않고 안내만 한다. */
		inRecommended() {
			return this.newBids.length >= 4 && this.newBids.length <= 6;
		},
		defaultTitle() {
			return '비워 두면 자동으로 주차 제목이 붙습니다';
		},
		/** 제목 옆 한 줄 집계. 표를 읽기 전에 규모를 알린다. */
		summary() {
			const n = this.list.length;
			if (!n) return '리포트 없음';
			const pub = this.list.filter((r) => r.status === 'published').length;
			const views = this.list.reduce((a, r) => a + (Number(r.view_cnt) || 0), 0);
			return `${n}건 · 발행 ${pub}건 · ${this.allLinks ? '고객' : '내 고객'} 열람 ${views}회`;
		},
		leadSummary() {
			const n = this.leads.length;
			if (!n) return this.allLinks ? '고객 링크 없음' : '내 고객 링크 없음';
			const viewed = this.leads.filter((l) => Number(l.view_cnt) > 0).length;
			const since = this.daysAgo(7);
			const recent = this.leads.filter((l) => l.last_viewed_at && l.last_viewed_at >= since).length;
			return `고객 링크 ${n}개 · 열어 본 고객 ${viewed}명 · 최근 7일 열람 ${recent}명`;
		},
		leadStaffTotal() {
			return this.leadStaff.reduce((a, s) => a + (Number(s.link_cnt) || 0), 0);
		},
		shownLeads() {
			const q = String(this.leadKw || '').trim();
			if (!q) return this.leads;
			return this.leads.filter((l) => String(l.client_label || '').includes(q) || String(l.title || '').includes(q)
				|| String(l.created_name || '').includes(q));
		},
		target() {
			return this.targets.find((t) => Number(t.report_uid) === Number(this.targetUid)) || null;
		},
		/** 고른 Weekly 에 남은 자리. 이미 담긴 매물은 건너뛰므로 실제로는 더 들어갈 수 있다(서버가 최종 판정). */
		roomText() {
			const t = this.target;
			if (!t) return '';
			const room = this.maxItems - (Number(t.item_cnt) || 0);
			const base = `지금 ${t.item_cnt}건이 담겨 있어 ${room}건까지 더 담을 수 있습니다. 이미 담긴 매물은 건너뜁니다.`;
			return this.newBids.length > room ? `${base} 선택한 ${this.newBids.length}건이 모두 새 매물이면 넘치니 줄여 주세요.` : base;
		},
		roomWarn() {
			const t = this.target;
			return !!t && this.newBids.length > this.maxItems - (Number(t.item_cnt) || 0);
		},
	},
	watch: {
		// 편집 화면에서 '목록' 으로 돌아오거나 주소의 tab 이 바뀌어도 화면이 따라간다.
		'$route.query.tab'(v) {
			this.tab = v === 'leads' ? 'leads' : 'list';
			if (this.tab === 'leads' && !this.leadsLoaded) this.loadLeads();
		},
	},
	created() {
		const raw = String(this.$route.query.bidList || '');
		const seen = new Set();
		// 숫자로만 된 항목만 받는다. parseInt 는 '123abc'·'456.9'·'1e3' 을 123·456·1 로 읽어, 오류 대신 엉뚱한 매물을
		// 담았다(Codex 리뷰). 형식이 틀린 항목이 있으면 알린다.
		const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
		const bad = parts.filter((s) => !/^\d{1,15}$/.test(s) || !Number.isSafeInteger(Number(s)) || Number(s) <= 0);
		if (bad.length) alert(`매물번호 형식이 올바르지 않은 항목 ${bad.length}건은 뺐습니다.`);
		this.newBids = parts.filter((s) => !bad.includes(s)).map(Number)
			.filter((n) => !seen.has(n) && seen.add(n));
		this.newType = this.$route.query.buildingType === 'rent' ? 'rent' : 'sell';
		this.tab = this.$route.query.tab === 'leads' ? 'leads' : 'list';
		this.loadList();
		if (this.newBids.length) this.loadTargets();
		if (this.tab === 'leads') this.loadLeads();
	},
	methods: {
		themeOf,
		ymd(v) {
			return String(v || '').slice(0, 10);
		},
		/** ms → "3분 20초" — 초 단위 숫자는 담당자에게 의미가 없다. */
		dur(ms) {
			const s = Math.round((Number(ms) || 0) / 1000);
			if (!s) return '-';
			if (s < 60) return `${s}초`;
			const m = Math.floor(s / 60);
			const r = s % 60;
			return r ? `${m}분 ${r}초` : `${m}분`;
		},
		/** 'YYYY-MM-DD HH:mm' 문자열(서버가 DB 시각으로 만든 것)과 같은 꼴로 n 일 전. 문자열 비교로 쓴다. */
		daysAgo(n) {
			const d = new Date(Date.now() - n * 86400000);
			const p = (x) => String(x).padStart(2, '0');
			return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
		},
		expired(l) {
			// 서버가 DB 시각으로 계산한 값을 쓴다(브라우저 시간대에 따라 갈리지 않게).
			return Number(l.is_expired) === 1;
		},
		setTab(t) {
			if (this.tab === t) return;
			// 주소에 남겨야 편집 화면에서 뒤로 왔을 때 같은 탭이 열린다. bidList 등 다른 인자는 그대로 둔다.
			this.$router.replace({ query: { ...this.$route.query, tab: t === 'leads' ? 'leads' : undefined } });
		},
		targetLabel(t) {
			const who = t.mine ? '' : ` · ${t.created_name || '다른 직원'}`;
			const full = Number(t.item_cnt) >= this.maxItems ? ' · 가득 참' : '';
			return `${t.period_key} · ${t.title} (${t.item_cnt}건 · ${t.status === 'published' ? '발행' : '작성중'}${who}${full})`;
		},
		loadList() {
			const qs = [];
			if (this.kw) qs.push(`kw=${encodeURIComponent(this.kw)}`);
			if (this.kind !== 'all') qs.push(`kind=${this.kind}`);
			if (this.isMaster && this.owner) qs.push(`owner=${this.owner}`);
			const q = qs.length ? `?${qs.join('&')}` : '';
			// 검색을 연달아 하면 늦게 온 앞 검색의 결과가 뒤 검색을 덮는다 — 마지막 요청의 응답만 받는다(Codex 리뷰).
			const seq = ++this.listSeq;
			this.$apiGET(`/admin/api/report/list${q}`).then((re) => {
				if (seq !== this.listSeq) return;
				if (!re) return;
				this.list = re.list || [];
				this.allLinks = !!re.allLinks;
				this.linkLevel = re.linkLevel || (re.allLinks ? 'all' : 'self');
				// 관리자인지는 서버 답을 따른다(화면의 mem_type 은 로그인 때 값이다).
				this.isMaster = !!re.master;
				this.authors = re.authors || [];
				if (re.maxItems) this.maxItems = Number(re.maxItems);
			});
		},
		pickKind(k) {
			if (this.kind === k) return;
			this.kind = k;
			this.loadList();
		},
		pickOwner(v) {
			this.owner = Number(v) || null;
			this.loadList();
		},
		/** 공용 지정·해제(관리자 전용, DE). 해제해도 이미 보낸 고객 링크는 그대로 열린다는 것을 알린다. */
		toggleShare(r) {
			if (this.sharing) return;
			const on = !r.shared;
			const msg = on
				? `'${r.title}' 을(를) 공용 Weekly 로 지정합니다.\nWeekly 권한이 있는 모든 직원의 목록에 보이고, 각자 자기 고객에게 링크를 발급할 수 있습니다.\n공용인 동안 내용은 관리자만 고칩니다.`
				: `'${r.title}' 의 공용 지정을 풉니다.\n이 Weekly 로 링크를 보낸 적이 없는 직원의 목록에서 빠지고, 누구도 새 링크를 발급할 수 없게 됩니다.\n이미 보낸 고객 링크는 그대로 열리고, 보낸 직원은 자기 링크의 열람과 회수만 합니다.`;
			if (!confirm(msg)) return;
			this.sharing = r.report_uid;
			this.$apiPOST('/admin/api/report/share', { reportUid: r.report_uid, shared: on ? 'Y' : 'N' }).then((re) => {
				if (re && re.ok) this.loadList();
			}).catch(() => {}).finally(() => { this.sharing = null; });
		},
		loadTargets() {
			// 서버 상한(200)까지 받는다. 50 으로 자르면 오래된 여유 있는 Weekly 가 선택지에서 빠진다(Codex 리뷰).
			this.$apiGET('/admin/api/report/list?editable=1&limit=200').then((re) => {
				this.targetsLoaded = true;
				if (!re) return;
				if (re.maxItems) this.maxItems = Number(re.maxItems);
				const me = (this.$store.getters.getUserInfo || {}).mem_uid;
				// 내가 만든 것을 위로(어드민은 전부 받는다). 같은 쪽 안에서는 최근 것이 위.
				this.targets = (re.list || []).filter((r) => r.can_edit).map((r) => ({
					...r, mine: Number(r.created_by) === Number(me),
				})).sort((a, b) => Number(b.mine) - Number(a.mine));
				const open = this.targets.find((t) => Number(t.item_cnt) < this.maxItems);
				this.targetUid = open ? open.report_uid : null;
			});
		},
		loadLeads() {
			const q = this.allLinks && this.leadMem ? `?mem=${this.leadMem}` : '';
			// 칩을 연달아 누르면 늦게 온 앞 응답이 뒤 선택을 덮는다 → 마지막 요청의 응답만 받는다(Codex 리뷰).
			const seq = ++this.leadSeq;
			this.$apiGET(`/admin/api/report/leads${q}`).then((re) => {
				if (seq !== this.leadSeq) return;
				this.leadsLoaded = true;
				if (!re) return;
				this.leads = re.list || [];
				this.leadStaff = re.staff || [];
				this.leadsTruncated = !!re.truncated;
				if (re.scope) {
					this.allLinks = !!re.scope.all;
					this.linkLevel = re.scope.level || (re.scope.all ? 'all' : 'self');
					// 서버가 고른 담당자를 받지 않았으면(그 사람이 다른 팀으로 갔다 등) 화면의 선택도 푼다(DC, codex 리뷰).
					if (this.leadMem && !re.scope.mem) this.leadMem = null;
				}
			});
		},
		pickLeadStaff(uid) {
			this.leadMem = uid;
			this.loadLeads();
		},
		createReport() {
			if (!this.newBids.length || this.busy) return;
			this.creating = true;
			this.$apiPOST('/admin/api/report/create', {
				title: this.newTitle,
				buildingType: this.newType,
				bidList: this.newBids,
				shared: this.isMaster && this.newShared ? 'Y' : 'N',
			}).then((re) => {
				this.creating = false;
				if (!re || !re.ok) return;
				if (re.skipped) alert(`매물 ${re.skipped}건은 찾을 수 없어 제외했습니다.`);
				this.$router.replace({ name: 'ReportEdit', params: { id: String(re.reportUid) } });
			}).catch(() => { this.creating = false; });
		},
		/**
		 * 고른 매물을 이미 있는 Weekly 에 더한다. 담는 규칙(이미 담긴 매물 건너뛰기, 최대 건수, 못 불러온
		 * 매물이 있으면 전체 거부)은 서버 /report/item/add 한 곳이 정한다 — 매물 상세의 'Weekly 저장' 과 같은 길이다.
		 */
		addToReport() {
			const t = this.target;
			if (!t || this.busy) return;
			this.adding = true;
			this.$apiPOST('/admin/api/report/item/add', {
				reportUid: t.report_uid,
				buildingType: this.newType,
				bidList: this.newBids,
			}).then((re) => {
				this.adding = false;
				if (!re || !re.ok) return;
				const added = Number(re.addedCnt) || 0;
				const already = Number(re.alreadyCnt) || 0;
				const msg = added
					? `매물 ${added}건을 '${t.title}' 에 추가했습니다.${already ? `\n이미 담겨 있던 ${already}건은 건너뛰었습니다.` : ''}`
					: `선택한 매물이 이미 모두 '${t.title}' 에 담겨 있습니다.`;
				alert(`${msg}\n\n새로 담은 매물은 맨 뒤에 붙습니다. 순서와 코멘트는 편집 화면에서 정리하세요.`);
				this.$router.replace({ name: 'ReportEdit', params: { id: String(t.report_uid) } });
			}).catch(() => { this.adding = false; });
		},
		openEdit(uid) {
			this.$router.push({ name: 'ReportEdit', params: { id: String(uid) } });
		},
	},
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/design-tokens.scss";

/* 콘솔 규칙(DESIGN.md)대로: 흰 섹션 위의 표가 주인공이고, 새로 만드는 자리도 라벨칸 + 값칸의
   입력 표다. 회색 상자·둥근 카드를 이 화면만 따로 만들지 않는다. */
.ra-new {
	margin-bottom: 20px;
	.section-tit.sub { margin-top: 4px; }
	td { vertical-align: top; }
	th { vertical-align: top; padding-top: 12px; }
}
.ra-col-label { width: 132px; }
.ra-row {
	display: flex;
	align-items: center;
	gap: 8px;
	.btn { white-space: nowrap; flex: 0 0 auto; }
}
.ra-title { max-width: 520px; }
.ra-target { max-width: 620px; height: 30px; font-size: 13px; }
.ra-note {
	@include dt-type-dense;
	color: $dt-color-ink-faint;
	margin-left: 10px;
	&--block { display: block; margin: 6px 0 0; }
	&--flush { margin-left: 0; }
	&.warn { color: $dt-color-warning-ink; font-weight: 600; }
}

/* 목록. 숫자는 가운데, 이름·제목은 왼쪽(DESIGN.md Typography). */
.table td.left { text-align: left; }
.table td.num { font-variant-numeric: tabular-nums; }
.table td.zero { color: $dt-color-ink-faint; }
.table tr.is-off td { color: $dt-color-ink-faint; }
.table tr.is-off td b { font-weight: 400; }
.ra-title-cell b { font-weight: 600; }
.ra-period {
	display: block;
	font-size: 11.5px;
	color: $dt-color-ink-faint;
	line-height: 1.3;
	margin-top: 1px;
}
.ra-top {
	display: block;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-weight: 600;
}
.ra-muted { color: $dt-color-ink-faint; }
.ra-empty {
	padding: 26px 8px !important;
	color: $dt-color-ink-faint;
}
.ra-guide { color: $dt-color-ink-faint; margin-bottom: 0; }
.ra-scope {
	@include dt-type-dense;
	color: $dt-color-ink-faint;
	margin: 0 0 10px;
}

/* 공용 표시도 배지가 아니라 잉크 한 단어다(제목 앞). 파랑은 누르는 것에만 쓴다(DESIGN.md). */
.ra-shared {
	color: $dt-color-ink;
	font-weight: 700;
	margin-right: 6px;
}
.ra-check {
	display: flex;
	align-items: center;
	gap: 6px;
	margin: 8px 0 0;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
	input { margin: 0; }
}
.ra-owner { width: auto; min-width: 140px; height: 26px; font-size: 12px; padding-top: 0; padding-bottom: 0; }

/* 상태는 배지가 아니라 색 잉크 한 단어. 표 안 인라인에 배경을 칠하지 않는다(DESIGN.md Don't). */
.ra-status {
	font-weight: 600;
	&.is-pub { color: $dt-color-success-ink; }
	&.is-draft { color: $dt-color-ink-faint; }
}

/* 담당자별 칩(어드민). 계약 관리의 직원별 칩(ContractAdmin .ct-staff)과 같은 생김새다. */
.ra-staff {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px;
	margin-bottom: 12px;
	max-height: 96px;
	overflow-y: auto;
	&__label {
		font-size: 12px;
		font-weight: 600;
		color: $dt-color-ink-muted;
		margin-right: 2px;
		&--gap { margin-left: 14px; }
	}
	// bootstrap .btn 과 특이도가 같으면 로드 순서에 따라 배경·테두리가 덮인다 → 부모를 함께 쓴다.
	.ra-staff__chip {
		height: 24px;
		padding: 0 10px;
		font-size: 12px;
		line-height: 1;
		border: 1px solid $dt-color-line-strong;
		border-radius: $dt-rounded-full;
		background: $dt-color-canvas;
		color: $dt-color-ink-muted;
		white-space: nowrap;
		&:hover { border-color: $dt-color-ink-faint; color: $dt-color-ink; }
		&.on {
			background: $dt-color-primary-ink;
			border-color: $dt-color-primary-ink;
			color: #fff;
		}
	}
	&__cnt {
		font-size: 11px;
		margin-left: 4px;
		opacity: 0.75;
		font-variant-numeric: tabular-nums;
	}
	&__legend {
		font-size: 11.5px;
		color: $dt-color-ink-faint;
		margin-left: 4px;
	}
}
/* 리드 표의 날짜·수치·담당자 이름은 한 줄로 선다(두 줄로 꺾이면 행 높이가 들쭉날쭉해 대조가 안 된다). */
.ra-leads td.num,
.ra-leads td.nowrap { white-space: nowrap; }

/* 지면 견본. 종이색 위에 강조색 한 조각이면 이름을 읽기 전에 어느 지면인지 잡힌다.
   값은 frontend/src/utils/reportThemes.js 의 테마와 손으로 맞춘 것이다. */
.ra-sw {
	display: inline-flex;
	vertical-align: -3px;
	width: 22px;
	height: 16px;
	margin-right: 7px;
	border: 1px solid $dt-color-line-strong;
	overflow: hidden;
	position: relative;
	i { position: absolute; display: block; }
	i:first-child { inset: 0; }
	i:last-child { left: 3px; bottom: 3px; width: 9px; height: 3px; }
}
.ra-sw--catalog i:first-child { background: #f7f2e8; }
.ra-sw--catalog i:last-child { background: #8f2b32; width: 8px; height: 8px; border-radius: 50%; left: auto; right: -2px; top: -2px; }
.ra-sw--noir i:first-child { background: #15130f; }
.ra-sw--noir i:last-child { background: transparent; border: 1px solid #c9a253; width: 12px; height: 8px; left: 4px; top: 3px; }
.ra-sw--aerial i:first-child { background: linear-gradient(160deg, #3f6275, #16202a); }
.ra-sw--aerial i:last-child { background: #0f6b8c; border: 1px solid #fff; width: 6px; height: 6px; border-radius: 50%; left: 8px; top: 4px; }
.ra-sw--swiss i:first-child { background: #fff; }
.ra-sw--swiss i:last-child { background: #e8590c; width: 5px; height: 5px; left: 3px; bottom: 3px; }
.ra-sw--swiss i:first-child::after { content: ''; position: absolute; left: 0; right: 0; top: 3px; height: 2px; background: #111; }
.ra-sw--field i:first-child { background: linear-gradient(#1f4d38 0 56%, #f6f3ea 56%); }
.ra-sw--field i:last-child { background: #16241c; }
.ra-sw--gallery i:first-child { background: #f3f0e9; }
.ra-sw--gallery i:last-child { background: #b9ad97; width: 10px; height: 7px; left: 6px; top: 3px; }
.ra-sw--mosaic i:first-child { background: repeating-linear-gradient(90deg, #3a3a3a 0 6px, #141414 6px 7px), #3a3a3a; }
.ra-sw--mosaic i:last-child { background: #c2643a; width: 6px; height: 6px; left: auto; right: 1px; top: 1px; }
</style>
